/* eslint-disable max-depth */

// about vuex
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import Errors from '../../../common/nodejs/errors';
import alert from '../../../share/alert';
import util from '../../../share/util/utils';
import testMode from '../../../share/util/testModeRepro';
import vehicle from '../../../share/util/vehicle';
import reprogram from '../../../share/util/reprogram';
import macKey from '../../../share/util/macKey';
import immobilizer from '../../../share/util/immobilizer';
import loading from '../../../share/components/vue/ProcessingIcon';
import config from '../../../share/config';
import handler from '../../../share/util/apiResultValidator';
import errorMapping from '../../../share/util/api/errorMapping';
import service from '../../../share/util/api/service';
import diagnoResource from '../../../share/util/diagnose';
import { EXECUTE_NAVIGATION_RESOURCES } from '../../../constants/navigationResources';
import api from '../../../common/api';
import session from '../../../share/session';
import i18nResourceLoader from '../../../i18n/geti18nLoad';
const vehicleTestMode = require('../../../data/testMode/vehicleInfo.json');
const publishedUnitListTestMode = require('../../../data/testMode/publishedUnits.json');


const Success = 'success';
const Error = 'error';

export default {
  data() {
    return {
      processing: true,
      loginStatus: 'none'
    };
  },

  components: {
    'processing-view': loading
  },

  watch: {
    async loginStatus(value) {
      if (value === Success) {
        // i18nリソースの取得開始（既にキャッシュに存在する場合はそれを利用する）
        const region = util.getPackJsonRegion();
        i18nResourceLoader.start(region, this.language);
        if (this.isLoginStatus) {
          this.processing = false;
          alert.showConfirmation(this, null, this.$t('message.latest_signin_confirmation'),
            async () => {
              // 状態を成功に設定
              await this.moveNextScreen();
            }, null, null, true);
        } else {
          await this.moveNextScreen();
        }
      }
      else {
        // エラーの場合はwaitアイコンを非表示にする。
        this.processing = false;
        // 連携失敗の場合はログアウトを実施する
        this.logout(this);
      }
    }
  },

  methods: {
    ...mapActions('app', ['setIsSkipStart', 'setBreadCrumbs']),
    ...mapActions('fileResources', ['getResourceData']),
    ...mapActions('masterResources', ['getTargetUnits', 'getVehicleModelDisplayNames']),
    ...mapActions('auth', ['externalLogin', 'logout']),
    ...mapActions('netAppConnector', ['getPairingStatus', 'setPairingAppId']),
    ...mapActions('reprogram', ['setProgress']),
    ...mapActions('cache', ['cacheVehicleInfo', 'cacheSystemTable']),
    ...mapActions('targetUnit', ['setTargetUnit']),
    ...mapActions('vehicle', ['setvoltage', 'setVehicle']),
    ...mapActions('userProperty', ['loadUserCache']),
    ...mapActions('systems', ['setExternalError']),
    ...mapActions('immobilizer', ['getImbMenu']),
    ...mapActions('macKey', ['getMacVisibility']),
    ...mapActions('table', ['getForDisplayMenu']),

    moveNextScreen: async function () {
      this.processing = true;
      // ユーザーに紐づくキャッシュ情報を取得
      this.loadUserCache();
      // リソースのマスター情報を取得
      this.getResourceData(true);
      // 対象ユニット一覧のマスター情報を取得
      await this.getTargetUnits(true);

      // Get master information of vehicle model display names list
      await this.getVehicleModelDisplayNames(true);

      // ユーザーの言語情報設定
      this.$i18n.locale = this.language;

      // 利用規約・個人情報の利用に同意済か確認して遷移画面を分岐
      if (this.isAgreement && this.isAgreementPersonalData) {
        // 前回接続済みのクラウドコネクターIDがあるかチェック
        if (this.lastPairingNetAppId) {
          const param = {
            this: this,
            netAppId: this.lastPairingNetAppId
          };
          const result = await this.getPairingStatus(param);
          // クラウドコネクターが接続済みの場合
          if (result.isConnected) {
            // Home画面に遷移しない場合は、blank画面でpack.jsonの反映を待ってから次の処理に進む
            await i18nResourceLoader.wait();
            const pairingStatus = result.response.data;

            // リプロ実行中のチェック
            if (reprogram.isRunningReprogram(pairingStatus.repro_status)) {
              const naviList = ['blank', 'home', 'menu', 'systemTable', 'suSystemTable', 'systemFileTable', 'systemInformation'];
              this.setProgressInfo(pairingStatus, naviList);

              // リプロ状況を設定
              this.$router.push({ name: 'execution', params: { response: JSON.stringify(pairingStatus) } });
              return;
            } else if (macKey.isRunningMacKey(pairingStatus.mac_status)) {
              // MAC鍵状況を設定
              const naviList = ['blank', 'home', 'menu'];
              this.setProgressInfo(pairingStatus, naviList);
              await this.setMacKeyProgressInfo(this.lastPairingNetAppId);
              // Set Mac Status
              this.$router.push({ name: 'macKey', params: { response: JSON.stringify(pairingStatus) } });
              return;
            } else if (immobilizer.isRunningImmobilizer(pairingStatus.imb_status)) {
              // イモビ状況を設定
              const naviList = ['blank', 'home', 'menu'];
              this.setProgressInfo(pairingStatus, naviList);
              await this.setImmobilizerProgressInfo(this.lastPairingNetAppId);
              // Set Imb Status
              this.$router.push({ name: 'immobilizer', params: { response: JSON.stringify(pairingStatus) } });
              return;
            } else if (this.directMove) {
              // TS側からの画面遷移の場合
              if (this.directMove.direct_name === 'menu' || this.directMove.direct_name === 'systemInformation' || this.directMove.direct_name === 'soldVehicleSystemTable') {
                this.processDirectMove(pairingStatus);
                return;
              }
            }

          }
        }

        // 利用規約を表示する場合とダイレクト連携以外の場合はhome画面へ遷移する
        this.$router.push({ name: "home" });
      }
      else {
        this.$router.push({ name: "termsAgreement" });
      }
      this.processing = false;
    },

    // VIN情報取得
    getVinInfo: async function () {
      let val = util.createResultInfo(config.POLLING_INITIALIZED);
      try {
        // 車種情報更新のリクエスト
        const responseVehicle = await service.getCall(service.REPRO, '/netApps/' + this.lastPairingNetAppId);
        handler.validate(
          handler.validateTypes.all,
          responseVehicle, this, null,
          () => {
            // 処理が成功したときのコールバック処理
            // 内部の車両情報を更新
            if (this.isTestMode) {
              const vehicleInfo = vehicleTestMode;
              vehicleInfo.id = this.lastPairingNetAppId;
              this.setVehicle(vehicleInfo);
            } else {
              this.setVehicle(responseVehicle.data);
            }
            val = util.createResultInfo(config.POLLING_COMPLETED);
          },
          (result) => {
            // 処理が失敗してる場合のコールバック処理
            val = util.createResultInfo(config.POLLING_FAILURE, result.message, result.behaviorType, result);
          }, null, false);
        return val;
      } catch (error) {
        // 予期しない例外が発生
        val = util.createResultInfo(config.POLLING_FAILURE,
          this.$t(errorMapping.getErrorMessageForCode(Errors.CLOUD_CONNECTOR_PAIRING_RSP_NOT_RECEIVED)),
          handler.behaviorTypes.other, error);
        this.showFailedMessage('message.common_label_error_message', error);
        return val;
      }
    },

    // エラーメッセージ表示処理
    showFailedMessage(errTitleId, errorInfo, page) {
      const error = errorInfo;
      // メンテナンスエラーの場合はエラー表示を変更する
      let messages = null;
      if (!error.validatorResult || error.validatorResult.causeType === handler.causeTypes.maintenanceError) {
        messages = error.message;
      }
      else {
        const errTitle = this.$t(errTitleId);
        messages = {
          mainMessage: errTitle,
          messageList: []
        };
        const messageFirst = {
          label: this.$t('message.common_label_error_message'),
          message: error.message
        };
        messages.messageList.push(messageFirst);
      }

      alert.showError(this, null, messages, () => {
        if (page) {
          this.$router.push(page); // pageが設定されている場合は指定されたページに移動
        } else {
          this.logout(this); // pageが設定されていない場合はlogoutを実行
        }
      });
    },

    setProgressInfo: function (pairingStatus, naviList) {
      // 自動接続はOFFに設定しておく
      this.setIsSkipStart(false);

      // 車両情報を設定する
      this.cacheVehicleInfo(pairingStatus);

      // 取得した電圧情報のみ vehicle に保存する
      if (pairingStatus.is_test_mode) {
        if (vehicleTestMode.vehicle && vehicleTestMode.vehicle.battery_voltage) {
          if (vehicleTestMode.vehicle.battery_voltage.is_valid) {
            this.setvoltage(vehicleTestMode.vehicle.battery_voltage.voltage);
          }
        }
        pairingStatus.vehicle.unit_id = testMode.testModeSystemNameDisplay(pairingStatus, publishedUnitListTestMode);
      } else {
        if (pairingStatus.vehicle.battery_voltage) {
          if (pairingStatus.vehicle.battery_voltage.is_valid) {
            this.setvoltage(pairingStatus.vehicle.battery_voltage.voltage);
          }
        }
      }

      // 対象ユニット情報を設定する
      const unitInfo = vehicle.createTargetUnitInfo(pairingStatus, true);
      // SU法規対象リプロの復帰の場合、Type2 ECU の表示名を変更
      if (pairingStatus.vehicle.is_su_flow) {
        unitInfo.type2DisplayUnitId = util.getType2DisplayUnitId(pairingStatus.vehicle.unit_id, this.targetUnits);
      }
      this.setTargetUnit(unitInfo);

      this.progressNavigation(naviList);
    },

    progressNavigation(naviList) {
      // information-main 配下を保持する
      const infoMainIndex = this.$router.options.routes.findIndex(item => item.name === 'informationMain');
      // information-main 配下子供の情報を保持
      const infoTabs = this.$router.options.routes[infoMainIndex];
      const infoTabsIndex = infoTabs.children.findIndex(item => item.name === 'informationTabs');

      // ナビゲーションの追加設定
      // 第三階層のページは、通信車両が SU 法規対象／非対象かで動的に変化する
      for (let index = 0; index < naviList.length; index++) {
        const name = naviList[index];
        const findIndex = this.$router.options.routes.findIndex(item => item.name === name);
        if (findIndex !== -1) {
          const routerData = this.$router.options.routes[findIndex];
          const data = util.createBreadCrumbData(routerData, EXECUTE_NAVIGATION_RESOURCES);
          this.setBreadCrumbs(data);
        }
        else {
          // 見つからなかった場合は information-main を検索する
          const secondIndex = this.$router.options.routes[infoMainIndex].children[infoTabsIndex].children.findIndex(item => item.name === name);
          if (secondIndex !== -1) {
            const routerData = this.$router.options.routes[infoMainIndex].children[infoTabsIndex].children[secondIndex];
            const data = util.createBreadCrumbData(routerData, EXECUTE_NAVIGATION_RESOURCES);
            this.setBreadCrumbs(data);
          }
        }
      }
    },

    setImmobilizerProgressInfo: async function (netAppId) {
      //execute following code if immobilizer processing
      // イモビライザー機能に必要な車種情報・メニュー項目情報を設定する
      await this.getImbMenu({ 'netAppId': netAppId });
    },

    setMacKeyProgressInfo: async function (netAppId) {
      //execute following code if mac-key processing
      // MAC鍵機能に必要な情報を設定する
      await this.getMacVisibility({ 'netAppId': netAppId });
    },

    async processDirectMove(pairingStatus) {
      // 車両のシステムモードを取得
      const systemMode = vehicle.getVehicleSystemMode(this.isTestMode, this.isTechnicalHq);
      // ペアリングアプリIDを設定
      this.setPairingAppId({ netAppId: this.lastPairingNetAppId, isTestMode: pairingStatus.is_test_mode });

      // VIN情報を取得
      const result = await this.getVinInfo();
      // VIN情報の取得が完了以外の場合、エラーメッセージを表示して処理を終了
      if (result.status !== config.POLLING_COMPLETED) {
        this.processing = false;
        this.showFailedMessage('message.error_update_vin_numeber', result.error, "home");
        return;
      }
      // VIN情報を再取得した状態を内部で再キャッシュ
      this.cacheVehicleInfo(this.vehicleInfo);
      // メニュー画面に遷移する為の情報を取得
      const response = await this.getForDisplayMenu({ pairingAppId: this.lastPairingNetAppId, systemMode: systemMode, operationScreen: this.operationScreen, modal: this });

      // エラーレスポンスを処理
      if (this.handleErrorResponse(response)) return;

      // ナビゲーションリストを取得
      const naviList = await this.getNavigationListForDirectMove(pairingStatus);
      // ナビゲーションリストが取得できない場合は処理を終了
      if (!naviList) return;

      // ナビゲーションを進める
      this.progressNavigation(naviList);
      // バッテリー電圧が有効な場合、電圧を設定
      if (pairingStatus.vehicle.battery_voltage?.is_valid) {
        this.setvoltage(pairingStatus.vehicle.battery_voltage.voltage);
      }

      // ローディング状態を解除し、ルーターを使って指定された画面に遷移
      this.loading = false;
      // 外部連携するサービスからリプログラミング画面に遷移する場合、CC優先権の取得が必要（SSM5R車/既販車）。
      if (this.directMove.direct_name !== 'menu') {
        // 外部連携するサービスと車両通信の競合を回避するため、CC優先権を取得する。
        // 優先権を取得できた場合、後続の処理を行う。
        const param = {
          type: "lock",
          service: "ssm5"
        };
        const enterCCResponse = await api.postCall(config.REPRO, `/netApps/${this.lastPairingNetAppId}/usagePriority`, param);
        handler.validate(handler.validateTypes.all, enterCCResponse, this, null, () => {
          // CC優先権が取得できない場合、優先権エラーを表示して、Menu画面に遷移する。
          if (!enterCCResponse.data.status) {
            alert.showError(this, null, this.$t('error.CLOUD_CONNECTOR_UNAVAILABLE'));
            this.$router.push({ name: "menu" });
          } else {
            this.$router.push({ name: this.directMove.direct_name });
          }
        }, (result) => {
          // 処理が失敗したときのコールバック処理
          if (result.behaviorType === handler.behaviorTypes.logout) {
            this.logout(this);
          }
        });
      } else {
        // 外部連携するサービスからMenu画面に遷移する場合、CC優先権は取得せずに画面遷移する。
        this.$router.push({ name: this.directMove.direct_name });
      }
    },

    handleErrorResponse(response) {
      // エラーレスポンスを処理
      const error = response?.error;
      if (!error) return false;

      this.processing = false;
      // 権限エラーの場合、エラーメッセージを表示しログアウト
      if (error.causeType === handler.causeTypes.permissionError) {
        alert.showError(this, null, this.$t(errorMapping.getErrorMessageForCode(Errors.USER_ACCESS_PERMISSION_ERROR)), () => {
          this.logout(this);
        });
      } else if (error.behaviorType === handler.behaviorTypes.logout) {
        // ログアウトが必要な場合、ログアウト処理を実行
        this.logout(this);
      } else {
        // その他のエラーの場合、一般的なエラーメッセージを表示しログアウト
        alert.showError(this, null, this.$t('message.error_vehicle_connect_title'), () => {
          this.logout(this);
        });
      }
      return true;
    },

    async getNavigationListForDirectMove(pairingStatus) {
      // 初期ナビゲーションリストを設定
      const naviList = ['blank', 'home', 'menu'];
      // 直接移動先がシステム情報の場合の処理
      if (this.directMove.direct_name === 'systemInformation') {
        // is_su_flowに応じて要素を選択
        const systemTable = pairingStatus.vehicle.is_su_flow
          ? 'suSystemTable'
          : 'systemTable';

        // 選択した要素を追加
        naviList.push(systemTable, 'systemFileTable', 'systemInformation');


        // システム情報に必要なデータを設定
        const system = {
          unitId: this.directMove.unit_id,
          fileId: this.directMove.pfc_id,
        };

        // テストモードの場合、SystemInformation用テストデータを設定
        if (this.isTestMode) {
          this.setupTestModeDataForSystemInformation();
        }

        // リプログラム可能か確認し、リプログラム出来ない場合、Homeへ遷移
        if (! await this.checkReprogrammableStatus(system.unitId, system.fileId)) {
          this.processing = false;
          alert.showError(this, null, this.$t('message.message_system_no_repro_data'), () => {
            this.$router.push({ name: "home" });
          });
          return null;
        }

        // システム情報を設定
        this.setSystemInformation(system);
      }
      return naviList;
    },

    setupTestModeDataForSystemInformation() {
      // テストモード用のデータを設定
      const publishedUnitListTestMode = require('../../../data/testMode/publishedUnits.json');
      this.systemTables = {
        published: publishedUnitListTestMode,
        unpublished: this.systemTable.unpublished
      };
      // システムテーブルをキャッシュ
      this.cacheSystemTable(this.systemTables);
    },

    // 対象のユニットが対象のPFCファイルIDでリプログラム可能か確認する 
    // 可能な場合はtrue、それ以外はfalse。
    // unitId: 対象のシステムのUnitID（直リンク時に指定されたパラメータ）
    // fileID: 対象のPFCファイルのID（直リンク時に指定されたパラメータ）
    async checkReprogrammableStatus(unitId, fileId) {
      const statuses = [config.PFC_STATUS_RELEASED, config.PFC_STATUS_TEMP_REGISTERED];

      for (const status of statuses) {
        const systemEntries = this.systemTable[status] || [];
        const isReprogrammable = await this.checkUnitReprogrammable(systemEntries, unitId, fileId, status);
        if (isReprogrammable) {
          return true;
        }
      }

      return false; // すべてのチェックを通過してもリプログラム不可の場合はfalseを返す
    },

    // UnitIDがSystemTableのsystemと一致するか確認する
    // reprogrammableInfo : リプロ情報
    // unitId: 対象のシステムのUnitID（直リンク時に指定されたパラメータ）
    // fileID: 対象のPFCファイルのID（直リンク時に指定されたパラメータ）
    // status: 公開リプロ・非公開リプロ
    async checkUnitReprogrammable(systems, unitId, fileId, status) {
      for (const system of systems) {
        if (system && system.id === unitId) {
          const reprogrammableInfo = system.reprogrammable_info || {};
          const isReprogrammable = await this.checkFileIdReprogrammable(reprogrammableInfo, unitId, fileId, status);
          if (isReprogrammable) {
            return true;
          }
        }
      }
      return false;
    },

    // FileIdが対象のUnitに紐づくPFCファイル一覧に含まれるか確認する
    // reprogrammableInfo : リプロ情報
    // unitId: 対象のシステムのUnitID（直リンク時に指定されたパラメータ）
    // fileID: 対象のPFCファイルのID（直リンク時に指定されたパラメータ）
    // status: 公開リプロ・非公開リプロ
    async checkFileIdReprogrammable(reprogrammableInfo, unitId, fileId, status) {
      for (const info of Object.values(reprogrammableInfo)) {
        if (info && info.reprogrammable_status === config.REPRO_REPROGRAMMABLE) {
          const endpoint = `/netApps/${this.lastPairingNetAppId}/${unitId}/reproTargets`;
          const params = {
            // TSから遷移する場合、通常リプロのみ受け付ける
            repro_type: 'normal',
            file_status: status,
          };

          try {
            const response = await service.getParamCall(service.REPRO, endpoint, params);
            const isReprogrammable = response.data.pfcs.some(pfc => pfc.pfc_id === fileId);
            return isReprogrammable;
          } catch (error) {
            this.showFailedMessage('message.common_label_error_message', error);
          }
        }
      }
      return false;
    },

    setSystemInformation(system = null) {
      // システムの個別情報画面に遷移
      const param = {
        unitId: parseInt(system.unitId),
        fileId: system.fileId,
        publishmentMode: true,
        directRecoveryToRpr: false,
        type2DisplayUnitId: util.getType2DisplayUnitId(system.unitId, this.targetUnits),
        paneConfig: {
          nextMessage: "",
          nextPaneBg: "",
          showWarning: undefined,
          isExecutable: true
        }
      };
      this.setTargetUnit(param);

      this.$emit('setIsSystemHeaderDisplay', true);
    }
  },

  computed: {
    ...mapState({
      language: state => state.systems.language,
      isAgreement: state => state.userProperty.isAgreement,
      isAgreementPersonalData: state => state.userProperty.isAgreementPersonalData,
      lastPairingNetAppId: state => state.userProperty.lastPairingNetAppId,
      loggedIn: state => state.auth.loggedIn,
      isSkipStart: state => state.app.isSkipStart,
      region: state => state.userProperty.region,
      targetUnits: state => state.masterResources.targetUnits,
      initLogin: state => state.app.initLogin,
      currentProvider: state => state.provider.currentProvider,
      directMove: state => state.app.directMove,
      isTestMode: state => state.netAppConnector.isTestMode,
      isTechnicalHq: state => state.userProperty.userInfo.isTechnicalHq,
      vehicleInfo: state => state.vehicle.vehicleInfo,
      systemTable: state => state.table.systemTable,
      samlAccountList: state => state.systems.samlAccountList,
      isLoginStatus: state => state.userProperty.loginStatus,
    }),
    // 診断系のAPIは複数画面から使用されているため
    // 現在の画面で使用されるAPIが診断/データ再生/個別診断のうちどの機能なのか判別する
    operationScreen() {
      return diagnoResource.operationFrom(this.$router.currentRoute.value.name);
    }
  },

  // router prames 対応
  props: ["code", "domain"],

  async mounted() {

    if (!this.initLogin) {
      this.$router.push('/');
      return;
    }
    // ログインが完了してるか確認する
    if (this.loggedIn) {
      // 状態を成功に設定
      this.loginStatus = Success;
    }
    else {
      // ログインが完了していない場合リクエストのパラメータをチェックする
      if (this.code) {
        const param = {
          modal: this,
          code: this.code,
          code_verifier: session.getSessionStorage("code_verifier"), // sessionStorageからcode_verifierを取得
          provider: this.currentProvider,
          redirect_url: window.location.origin
        };
        // 外部連携ログイン処理
        const { isSuccess, response } = await this.externalLogin(param);

        if (isSuccess) {
          // 状態を成功に設定
          this.loginStatus = Success;
        }
        else {
          this.processing = false;
          const errorCode = response && response.data ? response.data.code : null;
          alert.showError(this, null, this.$t(errorMapping.getErrorMessageForCode(errorCode)),
            () => {
              // 状態を失敗に設定
              this.loginStatus = Error;
              this.setExternalError(true);
            }
          );
        }
      }
      else {
        this.processing = false;
        alert.showError(this, null, this.$t('message.external_signin_error', ["URL parameter 'code' is missing."]),
          () => {
            // 状態を失敗に設定
            this.loginStatus = Error;
            this.setExternalError(true);
          }
        );
      }
    }
  },
};